import { getStoredAccessToken, getStoredRefreshToken, useAuthStore } from '@/stores/authStore'

const API_BASE_URL =
  import.meta.env.VITE_API_URL || (import.meta.env.PROD ? '/api' : 'http://localhost:3001/api')

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  body?: unknown
  headers?: Record<string, string>
  responseType?: 'json' | 'blob'
  skipAuthRefresh?: boolean
  skipAuthHeader?: boolean
}

type RefreshResponse = {
  code: number
  data: {
    user: {
      id: string
      username: string
      qqNumber: string
      avatar?: string
      role: 'user' | 'admin'
    }
    token: string
    refreshToken: string
    expiresIn: number
  }
  message: string
}

let refreshPromise: Promise<string | null> | null = null
const AUTH_REDIRECT_MESSAGE_KEY = 'dreamrealms-auth-redirect-message'

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function shouldIgnoreAuthRefresh(endpoint: string) {
  return [
    '/public/auth/login',
    '/public/auth/register',
    '/public/auth/verify',
    '/public/auth/forgot-password',
    '/public/auth/reset-password',
    '/public/auth/refresh',
  ].includes(endpoint)
}

function redirectToLogin() {
  if (typeof window === 'undefined') {
    return
  }

  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
  if (window.location.pathname === '/auth/login') {
    return
  }

  const params = new URLSearchParams()
  if (currentPath && currentPath !== '/auth/login') {
    params.set('from', currentPath)
  }

  const target = params.toString() ? `/auth/login?${params.toString()}` : '/auth/login'
  window.location.assign(target)
}

function storeAuthRedirectMessage(payload?: {
  title?: string
  detail?: string
  message?: string
}) {
  if (typeof window === 'undefined' || !payload) {
    return
  }

  const title = payload.title || payload.message
  const detail = payload.detail
  if (!title && !detail) {
    return
  }

  sessionStorage.setItem(
    AUTH_REDIRECT_MESSAGE_KEY,
    JSON.stringify({
      title,
      detail,
    })
  )
}

export function consumeAuthRedirectMessage(): { title?: string; detail?: string } | null {
  if (typeof window === 'undefined') {
    return null
  }

  const raw = sessionStorage.getItem(AUTH_REDIRECT_MESSAGE_KEY)
  if (!raw) {
    return null
  }

  sessionStorage.removeItem(AUTH_REDIRECT_MESSAGE_KEY)

  try {
    return JSON.parse(raw) as { title?: string; detail?: string }
  } catch {
    return null
  }
}

async function readAuthRedirectPayload(response: Response) {
  try {
    const payload: unknown = await response.clone().json()
    if (!isRecord(payload)) {
      return null
    }

    const data = isRecord(payload.data) ? payload.data : null
    return {
      title:
        (typeof data?.title === 'string' ? data.title : undefined) ||
        (typeof payload.message === 'string' ? payload.message : undefined),
      detail: typeof data?.detail === 'string' ? data.detail : undefined,
      message: typeof payload.message === 'string' ? payload.message : undefined,
    }
  } catch {
    return null
  }
}

function handleAuthExpired(payload?: { title?: string; detail?: string; message?: string }) {
  storeAuthRedirectMessage(payload)
  useAuthStore.getState().clearAuth()
  redirectToLogin()
}

async function rawFetch(endpoint: string, options: ApiOptions = {}) {
  const url = `${API_BASE_URL}${endpoint}`
  const headers: Record<string, string> = {
    ...options.headers,
  }

  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json'
  }

  if (!options.skipAuthHeader) {
    const token = getStoredAccessToken()
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }
  }

  const config: RequestInit = {
    method: options.method || 'GET',
    headers,
    credentials: 'include',
  }

  if (options.body) {
    config.body = options.body instanceof FormData ? options.body : JSON.stringify(options.body)
  }

  return await fetch(url, config)
}

async function refreshAccessToken(): Promise<string | null> {
  const currentRefreshToken = getStoredRefreshToken()
  if (!currentRefreshToken) {
    handleAuthExpired()
    return null
  }

  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const response = await rawFetch('/public/auth/refresh', {
          method: 'POST',
          body: { refreshToken: currentRefreshToken },
          skipAuthHeader: true,
          skipAuthRefresh: true,
        })

        const payload = (await response.json()) as RefreshResponse
        if (!response.ok) {
          throw new Error(payload.message || '刷新登录状态失败')
        }

        useAuthStore.getState().setAuth(
          payload.data.user,
          payload.data.token,
          payload.data.refreshToken
        )

        return payload.data.token
      } catch (error) {
        handleAuthExpired()
        return null
      } finally {
        refreshPromise = null
      }
    })()
  }

  return await refreshPromise
}

async function request<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const response = await rawFetch(endpoint, options)

  if (
    response.status === 401 &&
    !options.skipAuthRefresh &&
    !shouldIgnoreAuthRefresh(endpoint) &&
    getStoredRefreshToken()
  ) {
    const refreshedToken = await refreshAccessToken()
    if (refreshedToken) {
      const retryResponse = await rawFetch(endpoint, {
        ...options,
        skipAuthRefresh: true,
      })
      return await parseResponse<T>(retryResponse, options)
    }
  }

  if (response.status === 401 && !options.skipAuthRefresh && !shouldIgnoreAuthRefresh(endpoint)) {
    handleAuthExpired((await readAuthRedirectPayload(response)) || undefined)
  }

  return await parseResponse<T>(response, options)
}

async function parseResponse<T>(response: Response, options: ApiOptions): Promise<T> {
  if (options.responseType === 'blob') {
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    return (await response.blob()) as unknown as T
  }

  const contentType = response.headers.get('content-type')
  if (contentType && contentType.includes('application/json')) {
    const data: unknown = await response.json()

    if (!response.ok) {
      if (isRecord(data)) {
        const errorMessage =
          (typeof data.error === 'string' ? data.error : undefined) ||
          (typeof data.message === 'string' ? data.message : undefined)
        throw new Error(errorMessage || `HTTP error! status: ${response.status}`)
      }

      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return data as T
  }

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  return response as unknown as T
}

export const apiClient = {
  get: <T>(
    endpoint: string,
    options?: {
      responseType?: 'json' | 'blob'
      skipAuthRefresh?: boolean
      skipAuthHeader?: boolean
    }
  ) =>
    request<T>(endpoint, { method: 'GET', ...options }),
  post: <T>(
    endpoint: string,
    body?: unknown,
    options?: { headers?: Record<string, string>; skipAuthRefresh?: boolean; skipAuthHeader?: boolean }
  ) => request<T>(endpoint, { method: 'POST', body, ...options }),
  put: <T>(
    endpoint: string,
    body?: unknown,
    options?: { headers?: Record<string, string>; skipAuthRefresh?: boolean; skipAuthHeader?: boolean }
  ) => request<T>(endpoint, { method: 'PUT', body, ...options }),
  patch: <T>(endpoint: string, body?: unknown, options?: { skipAuthRefresh?: boolean }) =>
    request<T>(endpoint, { method: 'PATCH', body, ...options }),
  delete: <T>(
    endpoint: string,
    options?: { body?: unknown; skipAuthRefresh?: boolean; skipAuthHeader?: boolean }
  ) => request<T>(endpoint, { method: 'DELETE', ...options }),
}

export default apiClient
